import { $actionEvent } from '@/actions/actionEvent';
import { $pdf, $showPdf } from '@/components/PrintPane';
import { $richText } from '@/components/RichTextEditor';
import { $sheet, rerenderSheet } from '@/components/SheetView';
import {
  $highlight,
  $selections,
  getSelectedCells,
  iterateCellPoses,
} from '@/components/selection';
import { $highlightTable } from '@/components/utils';
import { ColRowStyle } from '@/model/ColRowStyle';
import { Cell } from '@/model/cell';
import type { BorderStyle } from '@/model/cellStyle';
import { Col } from '@/model/col';
import { RectRange } from '@/model/range';
import { Row } from '@/model/row';
import type { Sheet } from '@/model/sheet';
import { Table } from '@/model/table';
import { printSheet } from '@/pdf/print';
import { undoManager } from '@/undo';
import { Vector2 } from '@/utils/vec';

function borderTop(sheet: Sheet, range: RectRange, style: BorderStyle | null) {
  if (range.isPointRange()) {
    const cell = sheet.getCellAtOrDefault(range.min);
    sheet.setCell(cell.withStyle({ borderTop: style }));
    return;
  }

  if (range.isRowRange()) {
    const topRange = range.topRange();
    for (const cell of topRange.getCells(sheet)) {
      sheet.setCell(cell.withStyle({ borderTop: 'unset' }));
    }
    const row = sheet.getRowOrDefault(range.min.y);
    sheet.setRow(
      row.withStyle(
        new ColRowStyle().withFrom(range.min.x).withStyle({ borderTop: style }),
      ),
    );
    return;
  }

  for (let x = range.min.x; x <= range.max.x; x++) {
    const pos = new Vector2(x, range.min.y);
    const cell = sheet.getCellAtOrDefault(pos);
    sheet.setCell(cell.withStyle({ borderTop: style }));
  }
}

function borderLeft(sheet: Sheet, range: RectRange, style: BorderStyle | null) {
  if (range.isPointRange()) {
    const cell = sheet.getCellAtOrDefault(range.min);
    sheet.setCell(cell.withStyle({ borderLeft: style }));
    return;
  }

  if (range.isColRange()) {
    const leftRange = range.leftRange();
    for (const cell of leftRange.getCells(sheet)) {
      sheet.setCell(cell.withStyle({ borderTop: 'unset' }));
    }
    const col = sheet.getColOrDefault(range.min.x);
    sheet.setCol(
      col.withStyle(
        new ColRowStyle()
          .withFrom(range.min.y)
          .withStyle({ borderLeft: style }),
      ),
    );
    return;
  }

  for (let y = range.min.y; y <= range.max.y; y++) {
    const pos = new Vector2(range.min.x, y);
    const cell = sheet.getCellAtOrDefault(pos);
    sheet.setCell(cell.withStyle({ borderLeft: style }));
  }
}

function borderRight(
  sheet: Sheet,
  range: RectRange,
  style: BorderStyle | null,
) {
  if (range.max.x === Infinity) return;
  borderLeft(sheet, range.rightOfRange(), style);
}

function borderBottom(
  sheet: Sheet,
  range: RectRange,
  style: BorderStyle | null,
) {
  if (range.max.y === Infinity) return;
  borderTop(sheet, range.downOfRange(), style);
}

function borderHorizontal(
  sheet: Sheet,
  range: RectRange,
  style: BorderStyle | null,
) {
  // TODO: for isInfiniteRange, mayhaps make a sheet default style
  if (range.height <= 1 || range.isInfiniteRange()) return;

  range = new RectRange(range.min.max(0).add([0, 1]), range.max);
  if (range.isRowRange()) {
    for (let y = range.min.y; y <= range.max.y; y++) {
      borderTop(
        sheet,
        new RectRange(new Vector2(range.min.x, y), range.max),
        style,
      );
    }
    return;
  }

  if (range.isColRange()) {
    for (let x = range.min.x; x <= range.max.x; x++) {
      const col = sheet.getColOrDefault(x);

      sheet.setCol(
        col.withStyle(
          new ColRowStyle()
            .withFrom(range.min.y)
            .withStyle({ borderTop: style }),
        ),
      );
    }
    return;
  }

  for (const pos of range.iterateCellPoses()) {
    const cell = sheet.getCellAtOrDefault(pos);
    sheet.setCell(cell.withStyle({ borderTop: style }));
  }
}

function borderVertical(
  sheet: Sheet,
  range: RectRange,
  style: BorderStyle | null,
) {
  if (range.width <= 1 || range.isInfiniteRange()) return;

  range = new RectRange(range.min.max(0).add([1, 0]), range.max);
  if (range.isColRange()) {
    for (let x = range.min.x; x <= range.max.x; x++) {
      borderLeft(
        sheet,
        new RectRange(new Vector2(x, range.min.y), range.max),
        style,
      );
    }
    return;
  }

  if (range.isRowRange()) {
    for (let y = range.min.y; y <= range.max.x; y++) {
      const row = sheet.getRowOrDefault(y);
      sheet.setRow(
        row.withStyle(
          new ColRowStyle()
            .withFrom(range.min.x)
            .withStyle({ borderLeft: style }),
        ),
      );
    }
    return;
  }

  for (const pos of range.iterateCellPoses()) {
    const cell = sheet.getCellAtOrDefault(pos);
    sheet.setCell(cell.withStyle({ borderLeft: style }));
  }
}

function borderInfo(): { sheet: Sheet; range: RectRange } | undefined {
  const sheet = $sheet.get();
  if (!sheet) return;
  const selections = $selections.get();
  if (selections.length > 1) return;

  const highlight = $highlight.get();
  const selection = selections[0] ?? RectRange.point(highlight);

  if (selection.isPointRange() && !selection.start.equals(highlight)) return;

  return {
    sheet,
    range: selection,
  };
}

$actionEvent.ons({
  undo: () => undoManager.undo(),
  redo: () => undoManager.redo(),
  formatAsTable() {
    const sheet = $sheet.get();
    if (!sheet) return;

    const selections = $selections.get();

    if (selections.length !== 1) return;

    const selection = selections[0]!;

    if (selection.isInfiniteRange()) {
      return;
    }
    const tx = sheet.startTransaction();

    const table = new Table(
      sheet.getUniqueTableName(),
      new RectRange(selection.min.max(0), selection.max),
    ).withCreateHeaders(sheet);
    sheet.setTable(table);
    if (!sheet.mainTable) sheet.mainTable = table;
    rerenderSheet();

    tx.commit(`Create table ${table.name}`);
  },
  delete: () => {
    const sheet = $sheet.get();
    if (!sheet) return;
    const tx = sheet.startTransaction();
    if ($selections.get().length === 0) {
      const highlight = $highlight.get();
      sheet.removeCellValueAt(highlight);
      tx.commit(`Delete cell ${highlight.toString()}`);
      return;
    }
    const cells = getSelectedCells(sheet);
    for (const cell of cells) {
      sheet.removeCellValueAt(cell.pos);
    }
    tx.commit(`Delete ${cells.length} cells`);
  },
  borderOutside: style => {
    const info = borderInfo();
    if (!info) return;
    const { sheet, range } = info;
    const tx = sheet.startTransaction();
    borderTop(sheet, range, style);
    borderLeft(sheet, range, style);
    borderBottom(sheet, range, style);
    borderRight(sheet, range, style);
    tx.commit(`Set border outside ${range.toString()}`);
  },
  borderTop: style => {
    const info = borderInfo();
    if (!info) return;
    const { sheet, range } = info;
    const tx = sheet.startTransaction();
    borderTop(sheet, range, style);
    tx.commit(`Set border top ${range.toString()}`);
  },
  borderLeft: style => {
    const info = borderInfo();
    if (!info) return;
    const { sheet, range } = info;
    const tx = sheet.startTransaction();
    borderLeft(sheet, range, style);
    tx.commit(`Set border left ${range.toString()}`);
  },
  borderBottom: style => {
    const info = borderInfo();
    if (!info) return;
    const { sheet, range } = info;
    const tx = sheet.startTransaction();
    borderBottom(sheet, range, style);
    tx.commit(`Set border bottom ${range.toString()}`);
  },
  borderRight: style => {
    const info = borderInfo();
    if (!info) return;
    const { sheet, range } = info;
    const tx = sheet.startTransaction();
    borderRight(sheet, range, style);
    tx.commit(`Set border right ${range.toString()}`);
  },
  borderHorizontal: style => {
    const info = borderInfo();
    if (!info) return;
    const { sheet, range } = info;
    const tx = sheet.startTransaction();
    borderHorizontal(sheet, range, style);
    tx.commit(`Set border horizontal ${range.toString()}`);
  },
  borderVertical: style => {
    const info = borderInfo();
    if (!info) return;
    const { sheet, range } = info;
    const tx = sheet.startTransaction();
    borderVertical(sheet, range, style);
    tx.commit(`Set border vertical ${range.toString()}`);
  },
  borderInner: style => {
    const info = borderInfo();
    if (!info) return;
    const { sheet, range } = info;
    const tx = sheet.startTransaction();
    borderHorizontal(sheet, range, style);
    borderVertical(sheet, range, style);
    tx.commit(`Set border inner ${range.toString()}`);
  },
  borderAll: style => {
    const info = borderInfo();
    if (!info) return;
    const { sheet, range } = info;
    const tx = sheet.startTransaction();
    borderTop(sheet, range, style);
    borderLeft(sheet, range, style);
    borderBottom(sheet, range, style);
    borderRight(sheet, range, style);
    borderHorizontal(sheet, range, style);
    borderVertical(sheet, range, style);
    tx.commit(`Set border all ${range.toString()}`);
  },
  cellStyle: style => {
    if ($richText.get() !== null) {
      // Let the rich text editor handle this
      return;
    }
    const sheet = $sheet.get();
    if (!sheet) return;
    const selections = $selections.get();
    if (selections.length === 0) {
      const tx = sheet.startTransaction();
      const highlight = $highlight.get();
      sheet.setCell(sheet.getCellAtOrDefault(highlight).withStyle(style));
      tx.commit(`Set cell style ${highlight.toString()}`);
      return;
    }
    // TODO: figure out how to handle infinite ranges
    if (selections.some(s => s.isInfinite())) return;

    const tx = sheet.startTransaction();
    const poses = iterateCellPoses();
    for (const pos of poses) {
      sheet.setCell(sheet.getCellAtOrDefault(pos).withStyle(style));
    }
    tx.commit(`Set cells style`);
  },
  tableStyle: style => {
    const sheet = $sheet.get();
    if (!sheet) return;
    const table = $highlightTable.get();
    if (!table) return;
    const tx = sheet.startTransaction();
    sheet.setTable(table.withStyle(style));
    tx.commit(`Set table style`);
    rerenderSheet();
  },
  print: () => {
    const sheet = $sheet.get();
    if (!sheet) return;
    const selections = $selections.get();
    let range: RectRange | undefined;

    if (selections.length === 1) {
      const sel = selections[0]!;
      if (!sel.isPointRange()) range = sel;
    }
    printSheet({
      sheet,
      range,
      printSettings: {
        // A4 for now
        pageSize: new Vector2(612, 792),
        marginTop: 25,
        marginBottom: 25,
        marginLeft: 10,
        marginRight: 10,
      },
    }).then(pdf => {
      $pdf.set(pdf);
      $showPdf.set(true);
    });
  },
});
